/**
 * Overlay Showcase — shows the OBS overlay composited over sport footage.
 * This is the "what your broadcast looks like" preview.
 * 15 seconds at 30fps = 450 frames. Loops.
 *
 * Timeline:
 *  0-150    Rider bar visible on footage (slow Ken Burns)
 *  60-180   Focus card slides in on left
 *  180-300  Focus card out, scorecard appears center
 *  300-450  Scorecard out, leaderboard appears
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, Img, Easing } from "remotion";

const FOOTAGE = {
  // Actual wakeboard action shots
  water: "https://www.mastercraft.com/media/rcbfe2h1/dt-hero-3.webp",
  wake: "https://www.mastercraft.com/media/pizdqnbj/dt-1-13.webp",
};

const RIDERS = [
  { name: "Harley Clifford", initials: "HC", colour: "#3b82f6", score: 8.7, hits: 3, photo: "https://fliteboard.com/cdn/shop/articles/FLITE_blog_images_HarleyClifford_Cover_aea7bee1-fc3c-4055-a4eb-608d65b495a3_1024x.jpg" },
  { name: "Nic Rapa", initials: "NR", colour: "#22c55e", score: 7.2, hits: 2, photo: "https://www.mastercraft.com/media/rfakh2xy/dt-about_.jpg" },
  { name: "Dallas Friday", initials: "DF", colour: "#eab308", score: 9.0, hits: 3, photo: null },
  { name: "Amber Wing", initials: "AW", colour: "#ef4444", score: 6.5, hits: 1, photo: null },
  { name: "Steel Lafferty", initials: "SL", colour: "#a855f7", score: 8.1, hits: 2, photo: null },
];

export function OverlayShowcase() {
  const frame = useCurrentFrame();
  const bgScale = interpolate(frame, [0, 450], [1, 1.08], { extrapolateRight: "clamp" });

  // Cycle between two images
  const imgSrc = frame < 225 ? FOOTAGE.water : FOOTAGE.wake;

  return (
    <AbsoluteFill>
      {/* Background sport footage */}
      <Img
        src={imgSrc}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: `scale(${bgScale})`,
          filter: "brightness(0.65) saturate(1.1)",
        }}
      />

      {/* Heat name banner — top left, prominent */}
      <div style={{
        position: "absolute", top: 30, left: 40,
        display: "flex", alignItems: "center", gap: 12,
        background: "rgba(10,10,11,0.88)", border: "1px solid rgba(255,255,255,0.08)",
        padding: "10px 24px", backdropFilter: "blur(8px)",
      }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 8px #ef4444" }} />
        <span style={{ fontSize: 18, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: 1 }}>
          Finals — Men's Pro
        </span>
        <span style={{ fontSize: 12, color: "#52525b", fontFamily: "'JetBrains Mono', monospace" }}>
          Boardstock Invitational
        </span>
      </div>

      {/* Rider bar — always visible at bottom */}
      <RiderBar />

      {/* Focus card — frames 60-180 */}
      <Sequence from={60} durationInFrames={120}>
        <FocusCard />
      </Sequence>

      {/* Scorecard — frames 180-300 */}
      <Sequence from={180} durationInFrames={120}>
        <ScoreCard />
      </Sequence>

      {/* Leaderboard — frames 300-450 */}
      <Sequence from={300} durationInFrames={150}>
        <Leaderboard />
      </Sequence>
    </AbsoluteFill>
  );
}

function RiderBar() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: "clamp" });

  return (
    <div style={{
      position: "absolute", bottom: 40, left: 40, right: 40,
      display: "flex", gap: 10, opacity,
    }}>
      {RIDERS.map((r) => (
        <div key={r.name} style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "rgba(10,10,11,0.85)", border: "1px solid rgba(255,255,255,0.08)",
          padding: "8px 14px", backdropFilter: "blur(8px)",
        }}>
          {r.photo ? (
            <img src={r.photo} alt={r.name} style={{
              width: 32, height: 32, borderRadius: "50%", objectFit: "cover",
              border: `2px solid ${r.colour}`,
            }} />
          ) : (
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: r.colour, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700, color: "#fff", border: "2px solid rgba(255,255,255,0.15)",
            }}>
              {r.initials}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>{r.name}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "'JetBrains Mono', monospace" }}>{r.score.toFixed(1)}</span>
              <span style={{ fontSize: 9, color: "#52525b", fontFamily: "'JetBrains Mono', monospace" }}>{r.hits} hits</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FocusCard() {
  const frame = useCurrentFrame();
  const slideIn = interpolate(frame, [0, 18], [-350, 0], { extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const slideOut = interpolate(frame, [100, 120], [0, -350], { extrapolateRight: "clamp", easing: Easing.in(Easing.cubic) });
  const x = frame < 100 ? slideIn : slideOut;
  const opacity = frame < 100
    ? interpolate(frame, [0, 18], [0, 1], { extrapolateRight: "clamp" })
    : interpolate(frame, [100, 120], [1, 0], { extrapolateRight: "clamp" });

  return (
    <div style={{
      position: "absolute", left: 60, top: "40%", transform: `translateY(-50%) translateX(${x}px)`,
      background: "rgba(10,10,11,0.9)", border: "1px solid rgba(255,255,255,0.08)",
      padding: 28, backdropFilter: "blur(12px)", minWidth: 280, opacity,
    }}>
      <img src="https://fliteboard.com/cdn/shop/articles/FLITE_blog_images_HarleyClifford_Cover_aea7bee1-fc3c-4055-a4eb-608d65b495a3_1024x.jpg" alt="Harley Clifford" style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: "3px solid #3b82f6", marginBottom: 14 }} />
      <div style={{ fontSize: 24, fontWeight: 800, color: "#fff" }}>Harley Clifford</div>
      <div style={{ fontSize: 14, color: "#94a3b8", marginTop: 4 }}>&ldquo;The King&rdquo;</div>
      <div style={{ fontSize: 13, color: "#64748b", fontFamily: "'JetBrains Mono', monospace", marginTop: 6 }}>#1 — Gold Coast, AUS</div>
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 10, fontStyle: "italic", maxWidth: 240 }}>8x World Champion, X Games Gold</div>
    </div>
  );
}

function ScoreCard() {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 15], [0.9, 1], { extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const opacity = interpolate(frame, [0, 15, 100, 120], [0, 1, 1, 0], { extrapolateRight: "clamp" });

  const judges = [
    { label: "M. Torres", value: 8.5 },
    { label: "K. Chen", value: 9.0 },
    { label: "R. Davies", value: 8.5 },
    { label: "S. Park", value: 9.0 },
    { label: "J. Miller", value: 8.5 },
  ];

  return (
    <div style={{
      position: "absolute", left: "50%", top: "45%", transform: `translate(-50%, -50%) scale(${scale})`,
      background: "rgba(10,10,11,0.92)", border: "1px solid rgba(255,255,255,0.08)",
      padding: "32px 48px", backdropFilter: "blur(12px)", textAlign: "center", opacity,
    }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 24 }}>Harley Clifford</div>
      <div style={{ display: "flex", gap: 32, justifyContent: "center", marginBottom: 24 }}>
        {judges.map((j) => (
          <div key={j.label} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 9, color: "#64748b", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>{j.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>{j.value}</div>
          </div>
        ))}
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16 }}>
        <div style={{ fontSize: 9, color: "#64748b", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>Final (Average)</div>
        <div style={{ fontSize: 44, fontWeight: 800, color: "#3b82f6" }}>8.7</div>
      </div>
    </div>
  );
}

function Leaderboard() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 15, 130, 150], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  const scale = interpolate(frame, [0, 15], [0.95, 1], { extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });

  const sorted = [...RIDERS].sort((a, b) => b.score - a.score);

  return (
    <div style={{
      position: "absolute", left: "50%", top: "45%", transform: `translate(-50%, -50%) scale(${scale})`,
      background: "rgba(10,10,11,0.92)", border: "1px solid rgba(255,255,255,0.08)",
      padding: "28px 36px", backdropFilter: "blur(12px)", minWidth: 600, opacity,
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: 1, marginBottom: 20 }}>Finals — Men's Pro</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["#", "Rider", "Torres", "Chen", "Davies", "Park", "Miller", "Final"].map((h) => (
              <th key={h} style={{ textAlign: h === "Rider" ? "left" : "center", padding: "6px 10px", color: "#52525b", fontSize: 9, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={r.name}>
              <td style={{ padding: "8px 10px", color: "#71717a", fontFamily: "'JetBrains Mono', monospace", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "center", fontSize: 12 }}>{i + 1}</td>
              <td style={{ padding: "8px 10px", color: "#fff", fontWeight: 600, borderBottom: "1px solid rgba(255,255,255,0.03)", fontSize: 13 }}>{r.name}</td>
              {[0, 1, 2, 3, 4].map((j) => (
                <td key={j} style={{ padding: "8px 10px", textAlign: "center", color: "#d4d4d8", fontFamily: "'JetBrains Mono', monospace", borderBottom: "1px solid rgba(255,255,255,0.03)", fontSize: 12 }}>
                  {(r.score + (j % 2 === 0 ? -0.2 : 0.3)).toFixed(1)}
                </td>
              ))}
              <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: 800, color: i === 0 ? "#eab308" : "#3b82f6", fontSize: 16, fontFamily: "'JetBrains Mono', monospace", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                {r.score.toFixed(1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
